import type { TtsAdapter } from "@/adapters/contracts";
import type { TtsAdapterContext } from "@/adapters/tts/base";
import { LocalMossTtsAdapter } from "@/adapters/tts/local-moss/adapter";
import type { EngineProfile } from "@/runtime/events";

/**
 * Adapter factories. The engine profile string is the contract — code
 * elsewhere selects an adapter by `profile.tts` (or `.llm`, `.stt`, `.vad`).
 * Cloud adapters plug in here as they land.
 *
 * Naming convention for adapter IDs (matches `engine-profiles.ts`):
 *   local-<engine-id>    — runs on device, may delegate to a worker internally
 *   cloud-<provider-id>  — talks to a network provider over HTTP/WS
 *
 * The factory throws loudly on unknown ids rather than silently falling back:
 * we want an engine-profile typo to be caught at the thread-create boundary,
 * not silently mis-routed to a default adapter the user didn't ask for.
 */
export function createTtsAdapter(
  profile: EngineProfile,
  context: TtsAdapterContext,
): TtsAdapter {
  const id = profile.tts;

  switch (id) {
    case "local-moss":
      return new LocalMossTtsAdapter(context);
    default:
      if (id.startsWith("cloud-")) {
        throw new Error(`Cloud TTS adapter not yet implemented: ${id}`);
      }
      throw new Error(`Unknown TTS adapter id: ${id}`);
  }
}
